// VirementPrevu.tsx
import { toast } from "@/components/ui/use-toast";
import {useHidePostActions} from "@/auth/hooks/useHidePostActions";
import usePostData from "@/hooks/usePostData";
import { convertEuroToCurrency, convertToEuro } from "@/libs/amount";
import * as React from "react";

/** Montants en EUR côté backend */
export type PaymentRow = {
  id: string; // id backend (stringifiable) ou id temporaire "tmp-…"
  amount: number; // EUR en base
  at: Date; // Date
};

type Props = {
  value: PaymentRow[] | undefined; // montants en EUR
  realFunding: number;
  currencySelected: string; // "EUR" ou code devise projet
  projectCurrency?: any; // { id, code, symbol, ... }
  projectCurrencySymbol?: string; // ex: "Ar" pour l’UI
  title?: string;
  ensureOneRow?: boolean;
  onApiList: (apiRes: any) => void; // réponse identique à /real-fundings?by=project
};

export default function VirementPrevu({
  value,
  realFunding,
  currencySelected,
  projectCurrency,
  projectCurrencySymbol,
  title = "Virements prévus",
  ensureOneRow = false,
  onApiList,
}: Props) {
  const [rows, setRows] = React.useState<InputRow[]>([]);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const { postData } = usePostData<any>();
  const hidePostActions = useHidePostActions();

  // (Re)construit les lignes d’UI selon la devise affichée, avec formatage 2 décimales.
  React.useEffect(() => {
    let canceled = false;
    (async () => {
      const next = await normalizeInAsync(
        value,
        currencySelected,
        projectCurrency
      );
      if (!canceled) {
        setRows(next.length ? next : ensureOneRow ? [mkEmptyRow()] : []);
      }
    })();
    return () => {
      canceled = true;
    };
    // recalcul si la liste backend change, la devise affichée change ou la devise projet change
  }, [value, currencySelected, projectCurrency?.id, ensureOneRow]);

  const addRow = () => setRows((r) => [...r, mkEmptyRow()]);

  const removeRowLocal = (index: number) =>
    setRows((r) => {
      if (ensureOneRow && r.length <= 1) return r;
      const next = [...r];
      next.splice(index, 1);
      return next;
    });

  const updateAmount = (index: number, val: string) =>
    setRows((r) => {
      const next = [...r];
      next[index] = { ...next[index], amount: val };
      return next;
    });

  const updateDate = (index: number, val: string) =>
    setRows((r) => {
      const next = [...r];
      next[index] = { ...next[index], date: val };
      return next;
    });

  // formate l’input au blur avec 2 décimales
  const formatRowAmount = (index: number) =>
    setRows((r) => {
      const next = [...r];
      next[index] = {
        ...next[index],
        amount: formatAmountString(next[index].amount),
      };
      return next;
    });

  // ===== API: SAVE (add/edit) =====
  const saveRow = async (row: InputRow) => {
    const amtDisplay = parseAmount(row.amount); // montant saisi dans la devise affichée
    if (Number.isNaN(amtDisplay)) {
      toast({ title: "Montant invalide." });
      return;
    }
    const iso = inputDateToISO(row.date);
    if (!iso) {
      toast({ title: "Date invalide." });
      return;
    }

    // Convertir vers EUR pour l’API si affichage ≠ EUR
    let amountInEuro = amtDisplay;
    if (currencySelected !== "EUR" && projectCurrency?.code) {
      try {
        amountInEuro = Number(await convertToEuro(amtDisplay, projectCurrency));
      } catch {
        toast({ title: "Conversion devise → EUR impossible." });
        return;
      }
    }

    const payload: any = {
      realfunding: realFunding,
      amount: amountInEuro,
      at: iso,
    };

    const isPersisted = !String(row.id).startsWith("tmp-");
    if (isPersisted) payload.toEdit = row.id;

    try {
      setBusyId(row.id);
      const res = await postData({
        url: `${
          import.meta.env.VITE_API_ENDPOINT
        }/real-funding-expected-paid/save?by=project&onlyFunding=true`,
        data: payload,
      });
      if (
        res &&
        typeof res === "object" &&
        "success" in res &&
        res.success === false
      ) {
        throw new Error(res?.message || "Échec de la sauvegarde.");
      }
      onApiList(res);
      toast({
        title: isPersisted
          ? "Virement prévu mis à jour."
          : "Virement prévu ajouté.",
      });
    } catch (e: any) {
      toast({ title: e?.message || "Erreur lors de l’enregistrement." });
    } finally {
      setBusyId(null);
    }
  };

  // ===== API: DELETE =====
  const deleteRow = async (row: InputRow, index: number) => {
    if (String(row.id).startsWith("tmp-")) {
      removeRowLocal(index);
      return;
    }
    try {
      setBusyId(row.id);
      const res = await postData({
        url: `${
          import.meta.env.VITE_API_ENDPOINT
        }/real-funding-expected-paid/delete/${
          row.id
        }?by=project&onlyFunding=true`,
        data: {},
      });
      if (
        res &&
        typeof res === "object" &&
        "success" in res &&
        res.success === false
      ) {
        throw new Error(res?.message || "Échec de la suppression.");
      }
      onApiList(res);
      toast({ title: "Virement prévu supprimé." });
      removeRowLocal(index);
    } catch (e: any) {
      toast({ title: e?.message || "Erreur lors de la suppression." });
    } finally {
      setBusyId(null);
    }
  };

  const symbol =
    currencySelected === "EUR"
      ? "€"
      : projectCurrencySymbol ||
        projectCurrency?.symbol ||
        projectCurrency?.code ||
        "¤";

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">{title}</div>

      <div className="overflow-x-auto rounded border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-600 bg-gray-50">
              <th className="py-2 px-2">Montant ({currencySelected})</th>
              <th className="py-2 px-2">Date</th>
              <th className="py-2 px-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="py-3 px-2" colSpan={3}>
                  Aucun virement prévu.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const isBusy = busyId === row.id;
                return (
                  <tr key={row.id} className="border-t">
                    <td className="py-2 px-2">
                      <div
                        className={`flex items-stretch rounded border text-sm ${
                          isBusy ? "opacity-60 pointer-events-none" : ""
                        }`}
                      >
                        <span className="px-2 py-1 text-xs text-gray-700 bg-gray-50 border-r whitespace-nowrap">
                          {symbol}
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-full px-2 py-1 text-sm outline-none rounded-r"
                          placeholder="0,00"
                          value={row.amount}
                          onChange={(e) => updateAmount(idx, e.target.value)}
                          onBlur={() => formatRowAmount(idx)}
                          disabled={isBusy}
                        />
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="date"
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={row.date}
                        onChange={(e) => updateDate(idx, e.target.value)}
                        disabled={isBusy}
                      />
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className="flex justify-end gap-2">
                        {!hidePostActions && (
                          <button
                            type="button"
                            className="px-2 py-1 border rounded text-xs disabled:opacity-60"
                            onClick={() => saveRow(row)}
                            disabled={isBusy}
                          >
                            Save
                          </button>
                        )}
                        {!hidePostActions && (
                          <button
                            type="button"
                            className="px-2 py-1 border rounded text-xs text-red-600 disabled:opacity-60"
                            onClick={() => deleteRow(row, idx)}
                            disabled={
                              isBusy || (ensureOneRow && rows.length <= 1)
                            }
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-start">
        {!hidePostActions && (
          <button
            type="button"
            className="px-3 py-2 rounded border text-sm"
            onClick={addRow}
            disabled={busyId !== null}
          >
            Ajouter un virement prévu
          </button>
        )}
      </div>
    </div>
  );
}

/* ================= Types & utils ================= */

type InputRow = {
  id: string; // persisté = id backend; nouveau = "tmp-xxxx"
  amount: string; // texte saisi dans la devise affichée
  date: string; // "YYYY-MM-DD" ou ""
};

function mkEmptyRow(): InputRow {
  const tmp =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return { id: `tmp-${tmp}`, amount: "", date: "" };
}

async function normalizeInAsync(
  val: PaymentRow[] | undefined,
  currencySelected: string,
  projectCurrency?: any
): Promise<InputRow[]> {
  const base = Array.isArray(val) ? val : [];
  return Promise.all(
    base.map(async (r) => {
      const idStr = r.id ? String(r.id) : mkEmptyRow().id;
      let uiAmount = r.amount; // EUR
      if (currencySelected !== "EUR" && projectCurrency?.code) {
        try {
          uiAmount = Number(
            await convertEuroToCurrency(r.amount, projectCurrency)
          );
        } catch {
          // on garde r.amount si conversion KO
        }
      }
      return {
        id: idStr,
        amount: Number.isFinite(uiAmount) ? formatAmountNumber(uiAmount) : "",
        date: dateToInputValue(new Date(r.at)),
      };
    })
  );
}

function parseAmount(s: string): number {
  if (!s) return NaN;
  // retire espaces (normaux, insécables, fines insécables)
  const cleaned = s.replace(/[ \u00A0\u202F]/g, "");
  // si virgule présente, on considère ',' comme séparateur décimal (format FR)
  if (cleaned.includes(",")) {
    const tmp = cleaned.replace(/\./g, "").replace(",", ".");
    const n = Number(tmp);
    return Number.isFinite(n) ? n : NaN;
  }
  // sinon, on laisse les '.' comme décimales (format en-US)
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

function dateToInputValue(d: Date | undefined): string {
  if (!d || Number.isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function inputDateToISO(s: string): string | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  // ISO UTC minuit (évite décalages fuseau côté serveur)
  return new Date(s + "T00:00:00Z").toISOString();
}

// helpers d’affichage à 2 décimales
function formatAmountNumber(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
function formatAmountString(s: string): string {
  const n = parseAmount(s);
  return Number.isNaN(n) ? s : formatAmountNumber(n);
}
